// packages/web-ui/src/hooks/useContextWindowForModel.ts
//
// Look up the context-window size (tokens) for a given model from the
// merged `llm.contextWindows` setting. Drives the composer's "ctx: X%"
// indicator denominator.
//
// The model identifier passed in matches `SessionMeta.providerName` —
// for Azure OpenAI this is the deployment name (which by convention in
// this project matches the underlying SKU), for AWS Bedrock the
// model id (e.g. `anthropic.claude-sonnet-4-6`).
//
// Falls back to {@link FALLBACK_CONTEXT_MAX} when the settings store is
// still loading, when the user-configured value is missing, or when the
// stored value is malformed. The fallback matches the conservative
// 128k default the auto-compact subsystem uses as its budget.

import { useStore } from 'zustand';
import { qualifiedKey, type SettingsKey } from '@tessa-lean/base';
import { getDefaultSettingsStore, selectMergedByKey } from '../state/settings.js';

/**
 * Fallback context-window used when the merged-settings store has no entry
 * for the active model (e.g. settings still loading, or a new model the
 * `llm.contextWindows` map doesn't seed yet). 128k mirrors the auto-compact
 * `contextWindowBudget` default.
 */
export const FALLBACK_CONTEXT_MAX = 128_000;

const LLM_CONTEXT_WINDOWS_KEY: SettingsKey = 'llm.contextWindows';

/**
 * React hook — returns the configured context-window for the given model id.
 * Returns {@link FALLBACK_CONTEXT_MAX} when no per-model entry exists.
 */
export function useContextWindowForModel(modelId: string | null | undefined): number {
  const store = getDefaultSettingsStore();
  const entry = useStore(store, (s) =>
    selectMergedByKey(s, qualifiedKey(LLM_CONTEXT_WINDOWS_KEY)),
  );
  return resolveContextWindow(entry, modelId);
}

/**
 * Pure helper — extracts the per-model context window from a
 * `ResolvedSettingEntry`. Exported for unit-test coverage; the React hook
 * is a thin wrapper around it.
 */
export function resolveContextWindow(
  entry: { value?: unknown; defaultValue: unknown } | undefined,
  modelId: string | null | undefined,
): number {
  if (modelId === null || modelId === undefined || modelId.length === 0) {
    return FALLBACK_CONTEXT_MAX;
  }
  const raw =
    entry !== undefined && entry.value !== undefined ? entry.value : entry?.defaultValue;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return FALLBACK_CONTEXT_MAX;
  }
  const candidate = (raw as Record<string, unknown>)[modelId];
  if (typeof candidate !== 'number' || !Number.isFinite(candidate) || candidate <= 0) {
    return FALLBACK_CONTEXT_MAX;
  }
  return candidate;
}
